import type { InventoryValue, Statement } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import { DecoRule, OakMark, Wordmark } from '../components/brand';
import { Empty, ErrorBox, PageHead, PracticeBanner, QueryView } from '../components/ui';
import { addDays, formatDate, formatMoney, formatQty, isIsoDate, todayIso } from '../format';
import { useLocale } from '../i18n';

/** The last day of the month before the one a day falls in. */
export function lastMonthEnd(today: string): string {
  return addDays(`${today.slice(0, 7)}-01`, -1);
}

/** The stock's value as a spreadsheet: one row per product, the total last. */
export function inventoryCsv(v: InventoryValue): string {
  // quoted, and a name that starts like a formula (=, +, -, @) is kept as text when the file opens in a spreadsheet
  const q = (s: string | null) => `"${(/^[=+\-@]/.test(s ?? '') ? `'${s}` : (s ?? '')).replace(/"/g, '""')}"`;
  const lines = ['SKU,Product,Category,Unit,On hand,Value,Packs without a cost'];
  for (const r of v.rows) lines.push([q(r.sku), q(r.name), q(r.category), q(r.packUnit), r.onHand, r.value.toFixed(2), r.unvalued].join(','));
  lines.push([q(''), q('Total'), q(''), q(''), '', v.total.toFixed(2), ''].join(','));
  return lines.join('\n') + '\n';
}

/** Month-end (decision 106): the books closed through a day, the stock's value on a day, every statement printed. */
export function MonthEndPage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const owner = me?.role === 'owner';
  const books = useQuery({ queryKey: ['books'], queryFn: ({ signal }) => api.booksSettings(signal) });
  const [through, setThrough] = useState(lastMonthEnd(todayIso()));
  const setBooks = useMutation({ mutationFn: (d: string | null) => api.setBooksClosed(d), onSuccess: (b) => qc.setQueryData(['books'], b) });
  const [asOfRaw, setAsOf] = useState(lastMonthEnd(todayIso()));
  const asOf = isIsoDate(asOfRaw) ? asOfRaw : todayIso();
  const value = useQuery({ queryKey: ['reports', 'inventory-value', asOf], queryFn: ({ signal }) => api.inventoryValue(asOf, signal) });
  const download = (v: InventoryValue) => {
    const blob = new Blob([inventoryCsv(v)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `inventory-value-${v.asOf}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div className="stack">
      <PageHead title={t('monthEndTitle')} back="/" />
      <PracticeBanner />
      <section className="card stack-sm" data-testid="books">
        <h2>{t('booksClosedTitle')}</h2>
        <p className="muted">{t('booksClosedHint')}</p>
        <QueryView query={books}>
          {(b) => (
            <p data-testid="books-state">
              {b.closedThrough ? (
                <>
                  {t('booksClosedThrough')} <strong>{formatDate(b.closedThrough, intlLocale, 'long')}</strong>
                </>
              ) : (
                t('booksOpen')
              )}
            </p>
          )}
        </QueryView>
        {owner ? (
          <div className="contact-actions">
            <label className="field">
              <span>{t('closeThrough')}</span>
              <input type="date" value={through} max={addDays(todayIso(), -1)} onChange={(e) => setThrough(e.target.value)} />
            </label>
            <button type="button" className="btn btn-primary" disabled={setBooks.isPending || !isIsoDate(through)} onClick={() => window.confirm(t('closeBooksConfirm').replace('{date}', through)) && setBooks.mutate(through)}>
              {t('closeBooks')}
            </button>
            {books.data?.closedThrough ? (
              <button type="button" className="btn btn-ghost" disabled={setBooks.isPending} onClick={() => window.confirm(t('reopenBooksConfirm')) && setBooks.mutate(null)}>
                {t('reopenBooks')}
              </button>
            ) : null}
          </div>
        ) : null}
        {setBooks.error ? <ErrorBox error={setBooks.error} /> : null}
      </section>

      <section className="card stack-sm" data-testid="inventory-value">
        <h2>{t('inventoryValueTitle')}</h2>
        <p className="muted">{t('inventoryValueHint')}</p>
        <div className="contact-actions">
          <label className="field">
            <span>{t('asOfDay')}</span>
            <input type="date" value={asOfRaw} max={todayIso()} onChange={(e) => setAsOf(e.target.value)} />
          </label>
          {value.data ? (
            <button type="button" className="btn btn-ghost" onClick={() => download(value.data!)}>
              {t('downloadCsv')}
            </button>
          ) : null}
        </div>
        <QueryView query={value}>
          {(v) =>
            v.rows.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('product')}</th>
                      <th className="num">{t('onHand')}</th>
                      <th className="num">{t('value')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.rows.map((r) => (
                      <tr key={r.itemId}>
                        <td>
                          {r.name}
                          {r.category ? <span className="muted"> · {r.category}</span> : null}
                          {r.unvalued !== 0 ? <span className="badge badge-warn">{formatQty(r.unvalued)} {t('noCostShort')}</span> : null}
                        </td>
                        <td className={r.onHand < 0 ? 'num owed' : 'num'}>
                          {formatQty(r.onHand)} {r.packUnit}
                        </td>
                        <td className="num">{formatMoney(r.value, intlLocale)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2}>
                        <strong>{t('total')}</strong>
                      </td>
                      <td className="num" data-testid="inventory-total">
                        <strong>{formatMoney(v.total, intlLocale)}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )
          }
        </QueryView>
      </section>

      <section className="card stack-sm">
        <h2>{t('statementsTitle')}</h2>
        <p className="muted">{t('statementsHint')}</p>
        <Link className="btn btn-secondary" to="/reports/statements">
          {t('printStatements')}
        </Link>
      </section>
    </div>
  );
}

/** What the balance holds beyond the opening line and the open invoices: money on account (below zero) and the like. */
export function onAccount(s: Statement): number {
  const listed = (s.opening && s.balanceFrom ? s.opening : 0) + s.openInvoices.reduce((n, o) => n + o.balance, 0);
  return Math.round(((s.balance ?? 0) - listed) * 100) / 100;
}

/** Every statement with a balance, one per page (decision 106). */
export function StatementsPrintPage() {
  const { t, intlLocale } = useLocale();
  const query = useQuery({ queryKey: ['reports', 'statements'], queryFn: ({ signal }) => api.statementsPack(signal) });
  return (
    <QueryView query={query}>
      {(sts: Statement[]) => (
        <div className="stack route-print">
          <PageHead title={`${t('statementsTitle')} · ${sts.length}`} back="/money/month-end" />
          <div className="no-print">
            <button type="button" className="btn btn-primary" onClick={() => window.print()}>
              {t('print')}
            </button>
          </div>
          {sts.length === 0 ? <Empty>{t('noResults')}</Empty> : null}
          {sts.map((s) => (
            <div key={s.customerId} className="invoice print-page" data-testid="statement-page">
              <header className="letterhead print-only">
                <OakMark />
                <Wordmark />
                <DecoRule />
              </header>
              <section className="card stack-sm">
                <h2>
                  {t('statement')}: {s.customerName}
                </h2>
                <p className="muted">
                  {formatDate(todayIso(), intlLocale, 'long')} · {t('terms')} {s.termsDays} {t('days')}
                </p>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('invoice')}</th>
                        <th>{t('invoiceDate')}</th>
                        <th>{t('dueDate')}</th>
                        <th className="num">{t('balanceDue')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.opening !== 0 && s.balanceFrom ? (
                        <tr>
                          <td colSpan={3}>{t('openingLine').replace('{date}', formatDate(s.balanceFrom, intlLocale, 'date'))}</td>
                          <td className="num">{formatMoney(s.opening, intlLocale)}</td>
                        </tr>
                      ) : null}
                      {onAccount(s) !== 0 ? (
                        <tr>
                          <td colSpan={3}>{t('onAccountLine')}</td>
                          <td className="num">{formatMoney(onAccount(s), intlLocale)}</td>
                        </tr>
                      ) : null}
                      {s.openInvoices.map((o) => (
                        <tr key={o.id}>
                          <td>{o.invoiceNo}</td>
                          <td>{formatDate(o.date, intlLocale, 'date')}</td>
                          <td>
                            {o.dueDate ? formatDate(o.dueDate, intlLocale, 'date') : '—'}
                            {o.daysLate > 0 ? ` · ${o.daysLate} ${t('daysLate')}` : ''}
                          </td>
                          <td className="num">{formatMoney(o.balance, intlLocale)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3}>
                          <strong>{t('balance')}</strong>
                        </td>
                        <td className="num">
                          <strong>{s.balance === null ? '—' : formatMoney(s.balance, intlLocale)}</strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </section>
            </div>
          ))}
        </div>
      )}
    </QueryView>
  );
}
